import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Youtube, Facebook, Instagram, Radio, Music2, MonitorPlay } from "lucide-react";
import { API } from "@/lib/api";
import { buildPlayer, youtubeChat } from "@/lib/embed";

const SOCIAL_META = {
  youtube: { label: "YouTube", icon: Youtube },
  facebook: { label: "Facebook", icon: Facebook },
  tiktok: { label: "TikTok", icon: Music2 },
  instagram: { label: "Instagram", icon: Instagram },
};

export const LiveSection = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let timer;
    const load = () =>
      axios.get(`${API}/live`).then(({ data }) => setLive(data)).catch(() => {});
    load();
    timer = setInterval(load, 30000); // refresh live status every 30s
    return () => clearInterval(timer);
  }, []);

  if (!live) return null;

  const isLive = live.is_live && live.stream_url;
  const playerUrl = isLive
    ? buildPlayer(live.platform, live.stream_url)
    : live.offline_url
    ? buildPlayer(live.offline_platform, live.offline_url, false)
    : "";
  const chatUrl = isLive && live.show_chat && live.platform === "youtube" ? youtubeChat(live.stream_url) : "";

  const socials = Object.entries(live.socials || {}).filter(([, v]) => v);

  return (
    <section id="live" data-testid="live-section" className="relative bg-[#050505] py-24 md:py-36 border-t border-[#1a1a1a]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-5">
              {isLive ? (
                <span data-testid="live-badge" className="flex items-center gap-2 bg-red-600 text-white font-sub text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Live now
                </span>
              ) : (
                <span data-testid="offline-badge" className="label !text-zinc-500">Offline · Replay</span>
              )}
            </div>
            <h2 className="font-display uppercase text-white leading-[0.95] text-4xl sm:text-6xl lg:text-7xl">
              Watch us <span className="text-green glow-green">go live</span>
            </h2>
          </div>
          <p className="font-sub text-zinc-400 max-w-sm font-light">
            {isLive
              ? "We're on the streets right now. Tune in and be part of it."
              : "We're offline right now — here's our most recent stream. Follow us to catch the next one live."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div
            className={chatUrl ? "lg:col-span-8" : "lg:col-span-12"}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="relative w-full aspect-video bg-black border border-[#1a1a1a] overflow-hidden">
              {playerUrl ? (
                <iframe
                  data-testid="live-player"
                  title="Mask Giver live stream"
                  src={playerUrl}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              ) : (
                <div data-testid="live-empty" className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                  <MonitorPlay className="text-green mb-4" size={40} />
                  <p className="font-sub text-white text-lg">No stream set yet</p>
                  <p className="font-sub text-zinc-500 mt-1 max-w-sm">
                    Add a YouTube or Facebook stream link from the admin studio to start broadcasting here.
                  </p>
                </div>
              )}
            </div>
          </motion.div>

          {chatUrl && (
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <div className="w-full h-full min-h-[360px] aspect-video lg:aspect-auto bg-black border border-[#1a1a1a] overflow-hidden">
                <iframe
                  data-testid="live-chat"
                  title="Live chat"
                  src={chatUrl}
                  className="w-full h-full min-h-[360px]"
                />
              </div>
            </motion.div>
          )}
        </div>

        {socials.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="live-socials">
            <span className="label mr-2">{isLive ? "Also watch on" : "Follow for the next live"}</span>
            {socials.map(([key, url]) => {
              const meta = SOCIAL_META[key];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`live-social-${key}`}
                  className="btn-fill flex items-center gap-2 border border-[#1a1a1a] hover:border-green text-white px-5 py-2.5 rounded-full font-sub text-sm"
                >
                  <Icon size={16} /> {meta.label}
                  {isLive && <Radio size={13} className="text-red-500 animate-pulse" />}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
